import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import type { AnalyticsFlow } from 'pi-kiosk-shared';
import { reportClientError } from 'pi-kiosk-shared/logging';
import { captureException } from 'pi-kiosk-shared/sentry';
import { customerLogger } from '../logging/customerLogger.js';
import { emitCustomerErrorShown } from '../analytics/customerErrorAnalytics.js';
import type { CustomerEmitEventInput } from '../analytics/customerAnalyticsClient.js';
import { isTenantInactiveError } from '../api/tenantInactive.js';
import { isTenantEntitlementDeniedError } from '../api/tenantEntitlementDenied.js';
import { toCustomerFacingErrorMessage } from '../errors/toCustomerFacingErrorMessage.js';
import type { RecoveryClassSlug, RecoveryMsId } from '../recovery/types.js';
import { captureRecoveryReturnTo } from '../recovery/captureRecoveryReturnTo.js';
import { isOnRecoverySlug } from '../recovery/isCustomerRecoveryPath.js';
import { buildRecoveryPath, readRecoveryQueryContext } from '../recovery/useRecoveryNavigation.js';
import { showToast } from '../ui/toastStore.js';

/** Matches shop SLA ephemeral toast duration (SHOP_SLA_TOAST_DURATION_MS). */
export const CUSTOMER_ERROR_TOAST_DURATION_MS = 4000;

export interface CustomerErrorState {
  error: Error | null;
  message: string;
  isVisible: boolean;
}

export interface CustomerErrorHandlerAnalyticsOptions {
  readonly track: (input: CustomerEmitEventInput) => void;
  readonly flow?: AnalyticsFlow | null;
  readonly screenName?: string;
  readonly screen?: string;
  readonly resolveFailureReason?: (error: unknown, operation?: string) => string | null;
}

/**
 * Presentation for handler-owned failures (MS-REC-087 path).
 * Render throws are L1 / MS-REC-076 — never classified here.
 */
export type CustomerErrorHandlerPresentation = 'inline' | 'toast' | 'general-error';

/** Observable UI channel after applyClassificationUi (toast ≠ inline). */
export type CustomerErrorUiChannel = 'inline' | 'toast' | null;

export type CustomerErrorHandlerKind = 'field' | 'inventory' | 'payment' | 'async';

export interface ClassifyCustomerErrorInput {
  readonly kind?: CustomerErrorHandlerKind;
  /** Inventory/payment: navigate/general-error only when checkout cannot continue */
  readonly checkoutBlocked?: boolean;
}

export interface CustomerErrorClassification {
  readonly presentation: CustomerErrorHandlerPresentation;
  readonly recoveryClass: RecoveryClassSlug | null;
  readonly recoveryMsId: RecoveryMsId | null;
}

/**
 * Pure presentation metadata for handler-owned failures (ADR-013 / plan §10).
 * Does not navigate — callers apply UI via `applyClassificationUi`, which navigates
 * only when `presentation` is `general-error` (uses `recoveryClass` for the slug).
 *
 * | Kind | Presentation metadata |
 * | field | inline only — never recovery navigate |
 * | inventory / payment | toast, or recovery navigate when checkoutBlocked |
 * | payment/inventory + entitlement deny | access-denied (MS-REC-074), not general-error |
 * | async (unclassified 5xx) | general-error metadata / inline UI — never throw into EB |
 * | render throw | L1 → MS-REC-076 — not this handler |
 */
export function classifyCustomerErrorForHandler(
  input: ClassifyCustomerErrorInput = {},
  error?: unknown,
): CustomerErrorClassification {
  const kind = input.kind ?? 'async';

  if (kind === 'field') {
    return { presentation: 'inline', recoveryClass: null, recoveryMsId: null };
  }

  if (kind === 'inventory' || kind === 'payment') {
    if (input.checkoutBlocked === true) {
      // create-session / checkout write 403 entitlement → MS-REC-074 access-denied
      if (isTenantEntitlementDeniedError(error)) {
        return {
          presentation: 'general-error',
          recoveryClass: 'access-denied',
          recoveryMsId: 'MS-REC-074',
        };
      }
      return {
        presentation: 'general-error',
        recoveryClass: 'general-error',
        recoveryMsId: 'MS-REC-087',
      };
    }
    return { presentation: 'toast', recoveryClass: null, recoveryMsId: null };
  }

  // Unclassified async business failure → MS-REC-087 metadata (no EB throw)
  return {
    presentation: 'inline',
    recoveryClass: 'general-error',
    recoveryMsId: 'MS-REC-087',
  };
}

function resolveUserMessage(
  error: unknown,
  fallback: string,
  tenantInactiveMessage: string
): string {
  if (isTenantInactiveError(error)) {
    return tenantInactiveMessage;
  }
  return toCustomerFacingErrorMessage(error, { fallback });
}

function resolveTenantCode(
  tenantCodeParam: string | undefined,
  pathname: string,
): string | undefined {
  const fromParam =
    tenantCodeParam !== undefined && tenantCodeParam.trim().length > 0
      ? tenantCodeParam.trim()
      : undefined;
  if (fromParam !== undefined) {
    return fromParam;
  }
  const tenantFromPath = pathname.split('/').filter(Boolean)[0];
  return tenantFromPath !== undefined && tenantFromPath.length > 0 ? tenantFromPath : undefined;
}

export function useCustomerErrorHandler(
  context?: string,
  analyticsOptions?: CustomerErrorHandlerAnalyticsOptions
): {
  error: Error | null;
  /** Persistent inline / field alert message (not toast). */
  errorMessage: string;
  isErrorVisible: boolean;
  /** Ephemeral toast message (shop SLA-style); empty when toast channel idle. */
  toastMessage: string;
  isToastVisible: boolean;
  /** Last applied UI channel — toast and inline are never the same path. */
  errorChannel: CustomerErrorUiChannel;
  handleError: (
    error: unknown,
    operation?: string,
    classify?: ClassifyCustomerErrorInput,
  ) => void;
  reportVisibleError: (
    message: string,
    operation?: string,
    classify?: ClassifyCustomerErrorInput,
  ) => void;
  clearError: () => void;
} {
  const { t } = useTranslation('customer');
  const { tenantCode: tenantCodeParam } = useParams<{ tenantCode: string }>();
  const location = useLocation();
  const [errorState, setErrorState] = useState<CustomerErrorState>({
    error: null,
    message: '',
    isVisible: false,
  });
  const [toastState, setToastState] = useState<{ message: string; isVisible: boolean }>({
    message: '',
    isVisible: false,
  });
  const [errorChannel, setErrorChannel] = useState<CustomerErrorUiChannel>(null);
  const toastTimerRef = useRef<number | null>(null);

  const clearToastTimer = useCallback((): void => {
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearToastTimer();
    };
  }, [clearToastTimer]);

  const emitErrorShown = useCallback(
    (
      error: unknown,
      message: string,
      operation: string | undefined,
      classification: CustomerErrorClassification,
    ): void => {
      if (analyticsOptions === undefined) {
        return;
      }
      const scope = operation ?? context ?? 'customer';
      emitCustomerErrorShown({
        track: analyticsOptions.track,
        flow: analyticsOptions.flow ?? null,
        screenName: analyticsOptions.screenName,
        screen: analyticsOptions.screen,
        failureReason: analyticsOptions.resolveFailureReason?.(error, scope) ?? scope,
        error,
        operation: scope,
        message,
        ...(classification.recoveryClass !== null
          ? { recovery_class: classification.recoveryClass }
          : {}),
        ...(classification.recoveryMsId !== null
          ? { recovery_ms_id: classification.recoveryMsId }
          : {}),
      });
    },
    [analyticsOptions, context]
  );

  const applyClassificationUi = useCallback(
    (classification: CustomerErrorClassification, userMessage: string, error: unknown): void => {
      if (classification.presentation === 'toast') {
        // Ephemeral async toast via ToastHost — never journey ✓ pages; distinct from inline alert.
        clearToastTimer();
        setErrorState({ error: null, message: '', isVisible: false });
        setToastState({ message: userMessage, isVisible: true });
        setErrorChannel('toast');
        showToast({
          message: userMessage,
          variant: 'danger',
          durationMs: CUSTOMER_ERROR_TOAST_DURATION_MS,
        });
        toastTimerRef.current = window.setTimeout(() => {
          setToastState({ message: '', isVisible: false });
          setErrorChannel((prev) => (prev === 'toast' ? null : prev));
          toastTimerRef.current = null;
        }, CUSTOMER_ERROR_TOAST_DURATION_MS);
        return;
      }

      if (classification.presentation === 'inline') {
        clearToastTimer();
        setToastState({ message: '', isVisible: false });
        setErrorState({
          error: error instanceof Error ? error : new Error(userMessage),
          message: userMessage,
          isVisible: true,
        });
        setErrorChannel('inline');
        return;
      }

      // Recovery navigate (general-error or access-denied) — never throw into EB
      clearToastTimer();
      setToastState({ message: '', isVisible: false });
      const tenantCode = resolveTenantCode(tenantCodeParam, location.pathname);
      const recoverySlug = classification.recoveryClass ?? 'general-error';
      if (tenantCode !== undefined) {
        const queryCtx = readRecoveryQueryContext(location.search);
        const alreadyOnSlug = isOnRecoverySlug(location.pathname, recoverySlug);
        if (!alreadyOnSlug) {
          const returnTo = captureRecoveryReturnTo(
            location.pathname,
            location.search,
          );
          window.location.assign(
            buildRecoveryPath(tenantCode, recoverySlug, {
              ...(returnTo !== undefined ? { returnTo } : {}),
              ...(queryCtx.journey !== undefined
                ? { journey: queryCtx.journey }
                : {}),
            }),
          );
        }
      }
      setErrorState({
        error: error instanceof Error ? error : new Error(userMessage),
        message: userMessage,
        isVisible: true,
      });
      setErrorChannel('inline');
    },
    [clearToastTimer, location.pathname, location.search, tenantCodeParam],
  );

  const handleError = useCallback(
    (error: unknown, operation?: string, classify?: ClassifyCustomerErrorInput) => {
      const classification = classifyCustomerErrorForHandler(classify, error);
      const userMessage =
        classification.recoveryClass === 'access-denied'
          ? t('retail.rec.accessDenied.description')
          : resolveUserMessage(
              error,
              t('shared.unknownError'),
              t('retail.rec.tenant.description'),
            );
      const scope = operation ?? context ?? 'customer';
      customerLogger.error(`Customer error in ${scope}`, error, {
        operation: scope,
        presentation: classification.presentation,
        recoveryClass: classification.recoveryClass,
      });
      emitErrorShown(error, userMessage, scope, classification);
      reportClientError(error, {
        capture: (err, extra) => {
          captureException(err, extra);
        },
        extra: { operation: scope },
      });
      applyClassificationUi(classification, userMessage, error);
    },
    [applyClassificationUi, context, emitErrorShown, t]
  );

  const reportVisibleError = useCallback(
    (message: string, operation?: string, classify?: ClassifyCustomerErrorInput) => {
      const classification = classifyCustomerErrorForHandler(classify);
      const scope = operation ?? context ?? 'customer';
      const error = new Error(message);
      // G8 — sanitize before toast/inline (callers may pass raw API text).
      const publicMessage = toCustomerFacingErrorMessage(message, {
        fallback: t('shared.unknownError'),
      });
      emitErrorShown(error, publicMessage, scope, classification);
      applyClassificationUi(classification, publicMessage, error);
    },
    [applyClassificationUi, context, emitErrorShown, t]
  );

  const clearError = useCallback(() => {
    clearToastTimer();
    setErrorState({ error: null, message: '', isVisible: false });
    setToastState({ message: '', isVisible: false });
    setErrorChannel(null);
  }, [clearToastTimer]);

  return {
    error: errorState.error,
    errorMessage: errorState.message,
    isErrorVisible: errorState.isVisible,
    toastMessage: toastState.message,
    isToastVisible: toastState.isVisible,
    errorChannel,
    handleError,
    reportVisibleError,
    clearError,
  };
}
